import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { spawnDnaOrb } from './dna-orb'
import { dropBossItem, maybeDropItemForPredator } from './item-orb'
import { playSfx, panFor } from '../../engine/audio'
import type { EventBus } from '../events'
import { spawnInkSplatter, spawnPigmentPuff } from './particles'
import { BIOMES, currentBiome } from '../biomes'
import { currentTier } from '../evolution'
import { BOSSES } from '../bosses'
import { T } from '../tunables'

// Phase 9 — a player below the biome's home tier takes the biome's under-tier
// damage tax. Soft-gate teaching beat: "this part of the world is not built
// for you." At or above home tier, encounter cost matches the shallow pool.
export function playerDamageMultForBiome(player: Entity): number {
  const biome = BIOMES[currentBiome(player)]
  if (currentTier(player) >= biome.homeTier) return 1
  return biome.underTierDamageMult()
}

// Map enemy body radius → DNA payout. Regular predator (r=0.11) ~ 3–5,
// large (r=0.32) ~ 35–40. Smoothstep keeps small/large anchored and
// gives a gentle midrange.
export function dnaForRadius(r: number): number {
  const t = Math.min(1, Math.max(0, (r - 0.10) / (0.35 - 0.10)))
  const eased = t * t * (3 - 2 * t) // smoothstep
  return Math.max(1, Math.round(3 + eased * (40 - 3)))
}

// Cross-frame edge-detection state. Lives at module scope for the single
// game instance; reset on respawn so a previous life's HP can't suppress
// the next life's low-health cue.
let prevPlayerHp = -1
// Per-dash hit guard: tracks which predators have been hit in the current dash
// so a single dash only counts once per predator.
let damagedThisDash = new WeakSet<Entity>()
let prevDashActive = false

export function resetDamageState(): void {
  prevPlayerHp = -1
  damagedThisDash = new WeakSet<Entity>()
  prevDashActive = false
}

export function damageSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  // Tick player timers
  if (player.invincible !== undefined && player.invincible > 0) {
    player.invincible = Math.max(0, player.invincible - dt)
  }
  if (player.hitFlash !== undefined && player.hitFlash > 0) {
    player.hitFlash = Math.max(0, player.hitFlash - dt)
  }

  // Reset dash hit guard on the leading edge of a new dash.
  const dashActive = (player.dash?.activeRemaining ?? 0) > 0
  if (dashActive && !prevDashActive) {
    damagedThisDash = new WeakSet<Entity>()
  }
  prevDashActive = dashActive

  // Snapshot live queries for safe removal within the loop
  const allies = [...world.with('ally', 'position', 'renderable', 'health')]
  const predators = [...world.with('predator', 'position', 'renderable', 'health')]

  // Tick ally timers
  for (const ally of allies) {
    if (ally.invincible !== undefined && ally.invincible > 0) {
      ally.invincible = Math.max(0, ally.invincible - dt)
    }
    if (ally.hitFlash !== undefined && ally.hitFlash > 0) {
      ally.hitFlash = Math.max(0, ally.hitFlash - dt)
    }
  }

  // Tick predator hitFlash timers
  for (const pred of predators) {
    if (pred.hitFlash !== undefined && pred.hitFlash > 0) {
      pred.hitFlash = Math.max(0, pred.hitFlash - dt)
    }
  }

  const toRemove: Entity[] = []
  const playerR = player.renderable?.radius ?? 0.12

  for (const pred of predators) {
    if (toRemove.includes(pred)) continue

    const dx = pred.position.x - player.position.x
    const dy = pred.position.y - player.position.y
    const reach = pred.renderable.radius + playerR

    // Dash counter: player dashing + predator in windup or recovery → hit it.
    if (dashActive && (pred.predator!.state === 'windup' || pred.predator!.state === 'recovery')) {
      if (dx * dx + dy * dy < (reach + 0.05) * (reach + 0.05)) {
        if (!damagedThisDash.has(pred)) {
          damagedThisDash.add(pred)
          pred.health!.current -= 0.5
          pred.hitFlash = 0.18
          events.emit({ type: 'damage.dealt', target: pred, amount: 0.5 })
          playSfx('dash_impact', { volume: 0.5, pan: panFor(pred.position.x, player.position.x) })
          spawnInkSplatter(world, pred.position.x, pred.position.y)
          if (pred.health!.current <= 0) {
            dropDnaForPredator(world, pred)
            toRemove.push(pred)
          }
        }
      }
    }

    if (toRemove.includes(pred)) continue

    // Engulf absorb: player engulfing + non-chain-segment predator → absorb it.
    const engulfActive = (player.engulf?.activeRemaining ?? 0) > 0
    if (engulfActive && !pred.chainSegment) {
      if (dx * dx + dy * dy < reach * reach) {
        pred.hitFlash = 0.25
        dropDnaForPredator(world, pred)
        toRemove.push(pred)
        continue
      }
    }

    // Predator → player: only lunge state deals damage.
    if (pred.predator!.state === 'lunge') {
      if (dx * dx + dy * dy < reach * reach) {
        const shielded = player.shield && player.shield.activeRemaining > 0
        if (!shielded && (!player.invincible || player.invincible <= 0)) {
          // Guard (Padded hats etc.) scales all incoming damage via mods.
          const dmg = pred.predator!.damage * playerDamageMultForBiome(player)
            * (player.mods?.damageTakenMult ?? 1)
          player.health!.current -= dmg
          player.invincible = 1.2
          events.emit({ type: 'damage.taken', entity: player, amount: dmg })
          // Ink splatter at the player — wet smear / ripple feedback for the hit.
          spawnInkSplatter(world, player.position.x, player.position.y)
          // Spiteful (thorns): the biter takes damage back on a landed hit.
          const thorns = player.mods?.thornsDamage ?? 0
          if (thorns > 0) {
            pred.health!.current -= thorns
            pred.hitFlash = T.damage.hitFlashDuration
            events.emit({ type: 'damage.dealt', target: pred, amount: thorns })
            if (pred.health!.current <= 0) {
              dropDnaForPredator(world, pred)
              toRemove.push(pred)
            }
          }
          // low-health edge trigger
          if (prevPlayerHp > 1 && player.health!.current <= 1) {
            events.emit({ type: 'player.lowHealth' })
          }
        }
      }
    }

    // Predator → each ally
    for (const ally of allies) {
      if (toRemove.includes(ally)) continue
      const adx = pred.position.x - ally.position.x
      const ady = pred.position.y - ally.position.y
      const contactDist = pred.renderable.radius + ally.renderable.radius
      if (adx * adx + ady * ady < contactDist * contactDist) {
        if (!ally.invincible || ally.invincible <= 0) {
          ally.health!.current -= pred.predator!.damage
          ally.invincible = 0.4
          ally.hitFlash = T.damage.hitFlashDuration
          spawnInkSplatter(world, ally.position.x, ally.position.y)
          if (ally.health!.current <= 0) {
            toRemove.push(ally)
          }
        }
      }
    }

    // Ally (charging) → predator
    for (const ally of allies) {
      if (toRemove.includes(ally) || toRemove.includes(pred)) continue
      if (ally.ally?.state !== 'charging') continue
      if (ally.ally.targetPredator !== pred) continue
      const adx = pred.position.x - ally.position.x
      const ady = pred.position.y - ally.position.y
      const contactDist = pred.renderable.radius + ally.renderable.radius
      if (adx * adx + ady * ady < contactDist * contactDist) {
        const allyDmg = ally.ally.damage * (player.mods?.allyDamageMult ?? 1)
        pred.health!.current -= allyDmg
        pred.hitFlash = T.damage.hitFlashDuration
        events.emit({ type: 'damage.dealt', target: pred, amount: allyDmg })
        spawnInkSplatter(world, pred.position.x, pred.position.y)
        if (pred.health!.current <= 0) {
          dropDnaForPredator(world, pred)
          toRemove.push(pred)
          // Victory bloom in the ally's tint — paired with the ink splatter
          // already firing, the two-color "splat + bloom" reads as "your ally
          // just killed it" rather than a generic hit.
          const allyTint = ally.renderable?.tint ?? [0.3, 1.0, 0.45]
          spawnPigmentPuff(world, pred.position.x, pred.position.y, allyTint, pred.renderable.radius * 1.1)
          ally.ally.followTimer += 15
          ally.ally.state = 'returning'
          ally.ally.targetPredator = null
          playSfx('ally_kill', { volume: 0.4, pan: panFor(pred.position.x, player.position.x) })
        }
      }
    }
  }

  // ── Chain-boss segment damage ──────────────────────────────────────────────
  const chainSegments = [...world.with('chainSegment', 'health', 'position', 'renderable')]
  const bossSpec = BOSSES.chain

  // Tick hitFlash for chain segments.
  for (const seg of chainSegments) {
    if (seg.hitFlash !== undefined && seg.hitFlash > 0) {
      seg.hitFlash = Math.max(0, seg.hitFlash - dt)
    }
  }

  for (const seg of chainSegments) {
    if (toRemove.includes(seg)) continue
    const parent = seg.chainSegment!.parent
    const bossState = parent.boss?.state
    if (!bossState || bossState === 'dormant' || bossState === 'uncurling') continue

    const sdx = seg.position.x - player.position.x
    const sdy = seg.position.y - player.position.y
    const sReach = seg.renderable.radius + playerR

    // Dash counter: vulnerable in windup/recover/coil/recoil.
    const vulnerable =
      bossState === 'windup' || bossState === 'recover' ||
      bossState === 'coil'   || bossState === 'recoil'
    if (dashActive && vulnerable) {
      if (sdx * sdx + sdy * sdy < (sReach + 0.05) * (sReach + 0.05)) {
        if (!damagedThisDash.has(seg)) {
          damagedThisDash.add(seg)
          seg.health!.current -= 0.5
          seg.hitFlash = 0.18
          events.emit({ type: 'damage.dealt', target: seg, amount: 0.5 })
          playSfx('dash_impact', { volume: 0.5 })
          spawnInkSplatter(world, seg.position.x, seg.position.y)
          if (seg.health!.current <= 0) {
            dropDnaForSegment(world, seg)
            popFromChain(parent, seg)
            toRemove.push(seg)
            // Last segment: trigger boss kill.
            if (parent.chain!.segments.length === 0 && !toRemove.includes(parent)) {
              dropDnaForBossKill(world, parent)
              events.emit({ type: 'boss.killed', boss: parent, id: 'chain' })
              toRemove.push(parent)
            }
          }
        }
      }
    }

    if (toRemove.includes(seg)) continue

    // Boss → player: strike (head only). Coil used to hurt the player too, but
    // coil is ALSO the vulnerable window (dash it to deal damage) — the overlap
    // made dashing a coiling boss a coin-flip. monorail-228: coil is now a clean
    // "safe to attack" window; only the head's strike hurts the player.
    const isHead = seg.chainSegment!.index === 0
    const dealsDamage = bossState === 'strike' && isHead
    if (dealsDamage && sdx * sdx + sdy * sdy < sReach * sReach) {
      const shielded = player.shield && player.shield.activeRemaining > 0
      if (!shielded && (!player.invincible || player.invincible <= 0)) {
        const dmg = bossSpec.segmentDamage * playerDamageMultForBiome(player)
        player.health!.current -= dmg
        player.invincible = 1.2
        events.emit({ type: 'damage.taken', entity: player, amount: dmg })
        spawnInkSplatter(world, player.position.x, player.position.y)
        if (prevPlayerHp > 1 && player.health!.current <= 1) {
          events.emit({ type: 'player.lowHealth' })
        }
      }
    }
  }

  // Ally-death event before removal (narrator maps wipe vs single loss).
  const deadAllies = toRemove.filter(e => e.ally)
  if (deadAllies.length > 0) {
    events.emit({
      type: 'ally.died',
      count: deadAllies.length,
      remaining: allies.length - deadAllies.length,
    })
  }

  for (const e of toRemove) {
    if (world.id(e) !== undefined) world.remove(e)
  }

  prevPlayerHp = player.health?.current ?? prevPlayerHp
}

// A chain segment is open to damage in the same windows the dash counter uses.
// Shared so attack.ts and orbiters.ts (monorail-228: weapons opened up beyond
// the dash) gate on exactly the same "vulnerable" definition. Coil is included:
// it's the clean attack window now that coil no longer hurts the player.
export function chainSegmentVulnerable(seg: Entity): boolean {
  const s = seg.chainSegment?.parent.boss?.state
  return s === 'windup' || s === 'recover' || s === 'coil' || s === 'recoil'
}

// Apply chip damage to a vulnerable boss segment from a non-dash weapon
// (attack lash / orbiter). Handles flash, event, splatter, and the kill
// cascade (DNA drop → chain splice → boss-kill → removal) with immediate
// removal, mirroring how attack.ts already removes killed predators. Callers
// must have checked chainSegmentVulnerable first.
export function damageChainSegment(world: GameWorld, seg: Entity, amount: number, events: EventBus): void {
  seg.health!.current -= amount
  seg.hitFlash = 0.18
  events.emit({ type: 'damage.dealt', target: seg, amount })
  spawnInkSplatter(world, seg.position.x, seg.position.y)
  if (seg.health!.current > 0) return
  const parent = seg.chainSegment!.parent
  dropDnaForSegment(world, seg)
  popFromChain(parent, seg)
  if (world.id(seg) !== undefined) world.remove(seg)
  if (parent.chain!.segments.length === 0) {
    dropDnaForBossKill(world, parent)
    events.emit({ type: 'boss.killed', boss: parent, id: 'chain' })
    if (world.id(parent) !== undefined) world.remove(parent)
  }
}

function popFromChain(boss: Entity, seg: Entity): void {
  const segments = boss.chain!.segments
  const idx = segments.indexOf(seg)
  if (idx >= 0) segments.splice(idx, 1)
}

function dropDnaForSegment(world: GameWorld, seg: Entity): void {
  const total = dnaForRadius(seg.renderable!.radius)
  spawnDnaOrb(world, seg.position.x, seg.position.y, total)
}

function dropDnaForBossKill(world: GameWorld, boss: Entity): void {
  const { x, y } = boss.position
  const n = 4
  const per = 10
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.5
    const rad = 0.15 + Math.random() * 0.15
    spawnDnaOrb(world, x + Math.cos(a) * rad, y + Math.sin(a) * rad, per)
  }
  // Large DNA cache — the boss jackpot, replacing the retired mutation-orb
  // drop. Non-own DNA credits the growth-spurt meter (dna-orb.ts), so this
  // is a meaningful shove toward the next spurt card.
  spawnDnaOrb(world, x, y, 30)
  // Bosses always drop gear, never at floor rarity (A3). Offset so the
  // item orb doesn't stack under the DNA cache.
  dropBossItem(world, x + 0.35, y)
}

export function dropDnaForPredator(world: GameWorld, pred: Entity): void {
  const r = pred.renderable!.radius
  const total = dnaForRadius(r)
  const { x, y } = pred.position!
  spawnDnaOrb(world, x, y, total)
  // ~10% "nice extra": a small bonus DNA cache (replacing the retired mutation
  // orb). Still feeds the growth-spurt meter — a lucky-kill sweetener, not a
  // guaranteed reward.
  if (Math.random() < T.damage.predatorMutationDropChance) {
    spawnDnaOrb(world, x, y, Math.max(5, Math.round(total * 0.6)))
  }
  // Equipment roll (A3) — low chance, depth-scaled inside.
  maybeDropItemForPredator(world, pred)
}
